import React, { useState } from 'react'
import { createPortal } from 'react-dom'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { colors, layering } from '@mobi/component-library/Theme/Common'
import { addToast } from '@mobi/component-library/Feedback/Toast'
import { SvgLazy } from '@mobi/component-library/Common/Svg/SvgLazy'
import { colors as brandColors } from '@mobi/settings/colors'
import { AppRoutes } from '@core/AppRoutes'
import { closeModal } from '@core/Components/Modal'
import { QUICKBET_MODAL_ID } from '@core/Areas/Quickbet/constants'
import { setBetSlipIsOpen } from '@core/Areas/AppHeader/Store'
import { postShareToFollowMe } from '@core/Areas/FollowMe/helpers/api'
import { followMePrincipleProfileQuery } from '@core/Areas/FollowMe/helpers/queries'
import { handleApiErrorNotification } from '@core/Areas/FollowMe/helpers/handleApiErrorNotification'
import type { ShareBetData } from '@core/Areas/ShareBet/types'
import { trackShareBetClick } from '@core/Areas/ShareBet/ShareBetOutgoing/Components/ShareWithExternal/analytics'
import { FirstPostConfirmation } from '@core/Areas/FollowMe/Components/Common/FirstPostConfirmation'
import { shouldAllowShareInBetLoop } from '@core/Areas/FollowMe/helpers/shouldAllowShareInBetLoop'
import { ProfileAvatar } from '@core/Areas/FollowMe/Components/Common/ProfileAvatar'
import { trackFollowMeShareBetBlocked } from '@core/Areas/FollowMe/helpers/analytics'
import { navigateToMyProfileWithViewBet } from '@core/Areas/FollowMe/helpers/navigation'

export const ExpertHubButton: React.FC<{
  data: ShareBetData
  dismissTray: () => void
}> = ({ data, dismissTray }) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const [isFirstPostModalOpen, setFirstPostModalOpen] = useState(false)

  const { data: profileData, isLoading: isLoadingProfile } = useQuery(followMePrincipleProfileQuery)

  const { mutate } = useMutation({
    mutationFn: postShareToFollowMe,
    onSuccess: ({ postId }) => {
      const onToastViewButtonClick = () => {
        dispatch(closeModal({ id: QUICKBET_MODAL_ID }))
        dispatch(setBetSlipIsOpen(false))
        navigateToMyProfileWithViewBet({ postId })
      }
      addToast({
        id: 'addto-sharehub-success',
        isDismissable: false,
        title: 'Your bet was shared to Bet Loop',
        icon: 'success',
        button: { text: 'View', onClick: onToastViewButtonClick },
        theme: 'dark',
      })
      queryClient.invalidateQueries(followMePrincipleProfileQuery)
      trackShareBetClick(data, false, 'follow-me')
    },
    onError: async err => {
      handleApiErrorNotification(err)
    },
  })

  const postToExpertHub = () => {
    if (profileData) {
      mutate({ ticketSequence: data.share.ticketSequence as number })
    } else {
      dispatch(setBetSlipIsOpen(false))
      navigate(AppRoutes.BetLoop)
    }
  }

  const handleExpertHubClick = () => {
    if (profileData?.shareCount === 0) {
      setFirstPostModalOpen(true)
    } else {
      dismissTray()
      postToExpertHub()
    }
  }

  const handleFirstPostConfirm = () => {
    setFirstPostModalOpen(false)
    dismissTray()
    postToExpertHub()
  }

  const isShareAllowed = shouldAllowShareInBetLoop(data)

  const handleBlockShareFromBetLoop = () => {
    addToast({
      id: 'addto-sharehub-blocked',
      isDismissable: true,
      title: 'Your bet cannot be shared to Bet Loop',
      icon: 'warning',
      theme: 'dark',
    })
    trackFollowMeShareBetBlocked(data)
  }

  return (
    <>
      {isShareAllowed ? (
        <button onClick={handleExpertHubClick} disabled={isLoadingProfile} type='button'>
          {profileData ? (
            <ProfileAvatar avatar={profileData.avatar} color={profileData.color} size='sm' />
          ) : (
            <SvgLazy name='BetLoopHeaderLogo' width='7rem' color={brandColors.brand.primary} />
          )}
          Share to Bet Loop
        </button>
      ) : (
        <button
          onClick={handleBlockShareFromBetLoop}
          type='button'
          style={{
            backgroundColor: colors.neutral[200],
            borderColor: colors.neutral[300],
            color: colors.neutral[500],
          }}
        >
          {profileData ? (
            <div style={{ position: 'relative' }}>
              <span
                style={{
                  width: '100%',
                  height: '100%',
                  background: 'black',
                  opacity: '50%',
                  zIndex: 10,
                  position: 'absolute',
                }}
              >
                <ProfileAvatar avatar={profileData.avatar} color={profileData.color} size='sm' />
              </span>
            </div>
          ) : (
            <SvgLazy name='BetLoopHeaderLogo' width='7rem' color={colors.neutral[500]} />
          )}
          Share to Bet Loop
        </button>
      )}

      {createPortal(
        <FirstPostConfirmation
          isOpen={isFirstPostModalOpen}
          onConfirm={handleFirstPostConfirm}
          onCancel={() => setFirstPostModalOpen(false)}
          zIndex={layering.overlayHigh}
        />,
        document.body
      )}
    </>
  )
}
